import React, { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import Layout from '@/components/Layout';
import { ChefHat, Minus, Plus, Check, Calculator, Snowflake, Archive, ChevronDown } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Stepper({ label, value, suffix, min = 1, max = 99, onChange, testId }) {
  return (
    <div className="bg-[#FAF6EC] border border-[#1A4331]/10 rounded-2xl p-4 flex-1" data-testid={testId}>
      <p className="text-xs font-semibold text-[#7A6B57] uppercase tracking-wider mb-3">{label}</p>
      <div className="flex items-center justify-between">
        <button
          onClick={() => value > min && onChange(value - 1)}
          disabled={value <= min}
          className="w-9 h-9 rounded-full bg-[#F0F2EB] flex items-center justify-center text-[#1A4331] disabled:opacity-40 hover:bg-[#E5EBDD] transition-colors"
          data-testid={`${testId}-dec`}
        >
          <Minus className="w-4 h-4" />
        </button>
        <span className="text-2xl font-bold text-[#1A4331] text-center" style={{ fontFamily: 'Outfit, sans-serif' }}>
          {value}
          <span className="block text-[11px] font-medium text-[#7A6B57] tracking-normal">{suffix}</span>
        </span>
        <button
          onClick={() => value < max && onChange(value + 1)}
          disabled={value >= max}
          className="w-9 h-9 rounded-full bg-[#1A4331] flex items-center justify-center text-white disabled:opacity-40 hover:bg-[#255C44] transition-colors"
          data-testid={`${testId}-inc`}
        >
          <Plus className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

function PeakCard({ peak }) {
  const [open, setOpen] = useState(false);
  const [guide, setGuide] = useState(null);
  const [loading, setLoading] = useState(false);
  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && !guide) {
      setLoading(true);
      try {
        const r = await axios.get(`${API}/pantry/preservation/${peak.crop_id}`, { withCredentials: true });
        setGuide(r.data);
      } catch (e) {
        console.error('Failed to load preservation guide', e);
      } finally {
        setLoading(false);
      }
    }
  };
  const when = peak.ready ? 'Ready to harvest now' : `Peak in ~${peak.days_until} day${peak.days_until === 1 ? '' : 's'}`;
  return (
    <div className="bg-[#FAF6EC] rounded-2xl border border-[#C9A85B]/40 overflow-hidden" data-testid={`peak-card-${peak.crop_id}`}>
      <button onClick={toggle} className="w-full text-left p-4 flex items-center gap-3" data-testid={`peak-toggle-${peak.crop_id}`}>
        <div className="w-10 h-10 rounded-full bg-[#C9A85B]/20 flex items-center justify-center flex-shrink-0">
          <Archive className="w-5 h-5 text-[#8A6D2B]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-[#1A4331]">{peak.crop_name}</span>
            {peak.ready && <span className="text-[10px] font-bold uppercase bg-[#6B9F4A] text-white px-2 py-0.5 rounded-full">Now</span>}
          </div>
          <p className="text-xs text-[#7A6B57]">{when}{peak.for_meals?.length ? ` · for ${peak.for_meals.join(', ')}` : ''}</p>
        </div>
        <ChevronDown className={`w-5 h-5 text-[#7A6B57] transition-transform flex-shrink-0 ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="px-4 pb-4" data-testid={`peak-guide-${peak.crop_id}`}>
          {loading ? (
            <p className="text-xs text-[#7A6B57]">Loading preservation guide…</p>
          ) : guide ? (
            <>
              <p className="text-xs text-[#1A4331] italic mb-3">{guide.note}</p>
              <div className="space-y-2">
                {guide.methods.map((mth, i) => (
                  <div key={i} className="bg-white rounded-xl p-3 border border-[#1A4331]/5">
                    <p className="text-xs font-bold text-[#1A4331] uppercase tracking-wider">{mth.method}</p>
                    <p className="text-xs text-[#5C665C] mt-1 leading-snug">{mth.how}</p>
                  </div>
                ))}
              </div>
            </>
          ) : null}
        </div>
      )}
    </div>
  );
}

function PantryPlanner({ user }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const reqSeq = useRef(0);
  const dataRef = useRef(null);
  useEffect(() => { dataRef.current = data; }, [data]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/pantry/plan`, { withCredentials: true });
      dataRef.current = r.data;
      setData(r.data);
    } catch (e) {
      console.error('Failed to load pantry plan', e);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = (override) => {
    const cur = dataRef.current;
    if (!cur) return;
    const nextSettings = { ...cur.settings, ...override };
    const nextData = { ...cur, settings: nextSettings };
    dataRef.current = nextData; // update synchronously so rapid taps chain off latest
    setData(nextData);
    const myReq = ++reqSeq.current;
    axios.post(`${API}/pantry/plan`, nextSettings, { withCredentials: true })
      .then((r) => {
        if (myReq === reqSeq.current) { dataRef.current = r.data; setData(r.data); }
      })
      .catch((e) => console.error('Failed to save pantry plan', e));
  };

  const toggleMeal = (id) => {
    const cur = dataRef.current;
    if (!cur) return;
    const sel = new Set(cur.settings.meal_ids);
    if (sel.has(id)) sel.delete(id); else sel.add(id);
    save({ meal_ids: Array.from(sel) });
  };

  if (loading || !data) {
    return (
      <Layout user={user}>
        <div className="py-24 text-center text-[#7A6B57]" data-testid="pantry-loading">Loading your pantry plan…</div>
      </Layout>
    );
  }

  const s = data.settings;
  const selectedCount = s.meal_ids.length;

  return (
    <Layout user={user}>
      <div className="max-w-5xl mx-auto pb-10" data-testid="pantry-planner-page">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-[#1A4331] flex items-center justify-center flex-shrink-0">
            <ChefHat className="w-6 h-6 text-[#B4D330]" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-[#1A4331]" style={{ fontFamily: 'Outfit, sans-serif' }}>Pantry Planner</h1>
            <p className="text-sm text-[#7A6B57]">Plan your garden around the meals your family loves.</p>
          </div>
        </div>

        {/* Winter Pantry Readiness (Phase D) */}
        {selectedCount > 0 && (
          <div className="mt-6 bg-[#1A4331] rounded-3xl p-6 text-[#FAF6EC]" data-testid="winter-readiness">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <p className="text-[11px] uppercase tracking-[0.15em] text-[#B4D330] font-semibold flex items-center gap-1.5">
                  <Snowflake className="w-3.5 h-3.5" /> Winter pantry readiness
                </p>
                <h2 className="text-2xl font-bold mt-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {data.overall_readiness_pct}% ready for winter
                </h2>
                <p className="text-sm text-[#FAF6EC]/70 mt-1">
                  Across your {selectedCount} chosen {selectedCount === 1 ? 'meal' : 'meals'} — grow more of the crops below to close the gaps.
                </p>
              </div>
            </div>
            <div className="mt-4 h-3 bg-[#FAF6EC]/15 rounded-full overflow-hidden">
              <div className="h-full bg-[#B4D330] rounded-full transition-all" style={{ width: `${data.overall_readiness_pct}%` }} />
            </div>
          </div>
        )}

        {/* Step 1 — Meals */}
        <section className="mt-8">
          <div className="flex items-baseline gap-2">
            <span className="text-xs font-bold text-white bg-[#8EAA97] rounded-full w-5 h-5 flex items-center justify-center">1</span>
            <h2 className="text-lg font-bold text-[#1A4331]" style={{ fontFamily: 'Outfit, sans-serif' }}>Pick your family favourites</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
            {data.meals.map((m) => {
              const on = s.meal_ids.includes(m.id);
              return (
                <button
                  key={m.id}
                  onClick={() => toggleMeal(m.id)}
                  data-testid={`meal-card-${m.id}`}
                  className={`text-left rounded-3xl p-5 border-2 transition-all ${on ? 'border-[#1A4331] bg-[#FAF6EC] shadow-md' : 'border-transparent bg-[#FAF6EC]/60 hover:bg-[#FAF6EC]'}`}
                >
                  <div className="flex items-start justify-between">
                    <span className="text-4xl leading-none">{m.emoji}</span>
                    <span className={`w-6 h-6 rounded-full flex items-center justify-center transition-colors ${on ? 'bg-[#1A4331] text-white' : 'bg-[#E5EBDD] text-transparent'}`}>
                      <Check className="w-4 h-4" />
                    </span>
                  </div>
                  <h3 className="font-bold text-[#1A4331] mt-3">{m.name}</h3>
                  <p className="text-xs text-[#7A6B57] mt-1 leading-snug">{m.description}</p>
                  <p className="text-[11px] text-[#8EAA97] font-semibold mt-3 uppercase tracking-wider">{m.ingredients.length} garden crops</p>
                  {on && m.readiness_pct != null && (
                    <div className="mt-3" data-testid={`meal-readiness-${m.id}`}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[11px] font-semibold text-[#1A4331]">Pantry ready</span>
                        <span className="text-[11px] font-bold text-[#1A4331]">{m.readiness_pct}%</span>
                      </div>
                      <div className="h-1.5 bg-[#E5EBDD] rounded-full overflow-hidden">
                        <div className="h-full bg-[#6B9F4A] rounded-full transition-all" style={{ width: `${m.readiness_pct}%` }} />
                      </div>
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </section>

        {/* Step 2 — Household */}
        <section className="mt-10">
          <div className="flex items-baseline gap-2">
            <span className="text-xs font-bold text-white bg-[#8EAA97] rounded-full w-5 h-5 flex items-center justify-center">2</span>
            <h2 className="text-lg font-bold text-[#1A4331]" style={{ fontFamily: 'Outfit, sans-serif' }}>How much will you eat?</h2>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 mt-4">
            <Stepper label="Servings" value={s.servings} suffix="people" onChange={(v) => save({ servings: v })} testId="stepper-servings" />
            <Stepper label="Frequency" value={s.times_per_week} suffix="per week" onChange={(v) => save({ times_per_week: v })} testId="stepper-times" />
            <Stepper label="Season" value={s.season_weeks} suffix="weeks" max={40} onChange={(v) => save({ season_weeks: v })} testId="stepper-weeks" />
          </div>
          <p className="text-sm text-[#7A6B57] mt-3">
            That&apos;s about <b className="text-[#1A4331]">{data.serves_planned.toLocaleString()}</b> servings to grow for across the season.
          </p>
        </section>

        {/* Step 3 — Calculator */}
        <section className="mt-10">
          <div className="flex items-baseline gap-2">
            <span className="text-xs font-bold text-white bg-[#8EAA97] rounded-full w-5 h-5 flex items-center justify-center">3</span>
            <h2 className="text-lg font-bold text-[#1A4331] flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <Calculator className="w-5 h-5" /> Your planting plan
            </h2>
          </div>

          {selectedCount === 0 ? (
            <div className="mt-4 bg-[#FAF6EC] rounded-2xl p-8 text-center text-[#7A6B57] border border-[#1A4331]/10" data-testid="pantry-empty">
              Pick at least one meal above to see exactly how many of each crop to grow.
            </div>
          ) : (
            <div className="mt-4 space-y-2" data-testid="pantry-calculator">
              {data.crops.map((c) => (
                <div key={c.crop_id} className="bg-[#FAF6EC] rounded-2xl p-4 border border-[#1A4331]/10 flex items-center gap-4" data-testid={`calc-row-${c.crop_id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-semibold text-[#1A4331] truncate">{c.crop_name}</span>
                      <span className="text-xs text-[#7A6B57] flex-shrink-0">{c.needed_kg} kg needed</span>
                    </div>
                    <div className="mt-2 h-2 bg-[#E5EBDD] rounded-full overflow-hidden">
                      <div className="h-full bg-[#6B9F4A] rounded-full transition-all" style={{ width: `${c.coverage_pct}%` }} />
                    </div>
                  </div>
                  <div className="text-right w-24 flex-shrink-0">
                    <p className="text-2xl font-bold text-[#1A4331] leading-none" style={{ fontFamily: 'Outfit, sans-serif' }}>{c.needed_plants}</p>
                    <p className="text-[11px] text-[#7A6B57] mt-1">plants · {c.planted} in</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Preserve the peak (Phase C) */}
        {selectedCount > 0 && (
          <section className="mt-10" data-testid="preservation-section">
            <h2 className="text-lg font-bold text-[#1A4331] flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <Archive className="w-5 h-5" /> Preserve the peak
            </h2>
            <p className="text-sm text-[#7A6B57] mt-1">When a meal crop hits its harvest peak, put the glut away for winter.</p>
            {data.peaks && data.peaks.length > 0 ? (
              <div className="mt-4 space-y-2">
                {data.peaks.map((p) => <PeakCard key={p.crop_id} peak={p} />)}
              </div>
            ) : (
              <div className="mt-4 bg-[#FAF6EC] rounded-2xl p-6 text-center text-[#7A6B57] border border-[#1A4331]/10" data-testid="peaks-empty">
                No crops are at their peak yet — we&apos;ll flag each one here as it nears harvest so you can preserve the glut.
              </div>
            )}
          </section>
        )}
      </div>
    </Layout>
  );
}

export default PantryPlanner;
